import { useTranslation } from 'react-i18next';

/**
 * Aktiv / Inaktiv / Alle über einer Liste.
 *
 * Der Grund ist schlicht: was abgelegt, beendet oder gelöscht ist, steht sonst
 * zwischen dem, womit man arbeitet. Es wegzulassen wäre falsch — man sucht es
 * gelegentlich —, es immer zu zeigen aber auch.
 *
 * Die Zahlen stehen mit dran, weil ein leerer Reiter sonst wie ein Fehler
 * aussieht: „inaktiv (0)" sagt, dass nichts da ist; ein leerer Reiter ohne Zahl
 * lässt einen suchen.
 *
 * Voreinstellung ist **aktiv**. Das ist die Ansicht, in der man neunmal von
 * zehn arbeitet.
 */
export type ListenSicht = 'aktiv' | 'inaktiv' | 'alle';

export const LISTEN_SICHTEN: ListenSicht[] = ['aktiv', 'inaktiv', 'alle'];

export default function ListenReiter({
  sicht,
  onWechsel,
  zaehler,
}: {
  sicht: ListenSicht;
  onWechsel: (s: ListenSicht) => void;
  zaehler: Record<ListenSicht, number>;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-1 border-b border-slate-200 px-3">
      {LISTEN_SICHTEN.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onWechsel(s)}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${
            sicht === s
              ? 'border-brand-600 font-medium text-brand-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          {t(`listenSicht.${s}`)}
          <span
            className={`ml-1.5 tabular-nums ${
              sicht === s ? 'text-brand-500' : 'text-slate-400'
            }`}
          >
            {zaehler[s]}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Teilt eine Liste in die drei Sichten und zählt sie.
 *
 * `istAktiv` entscheidet die Zugehörigkeit — je Liste anders, denn „inaktiv"
 * heisst bei einem Server etwas anderes als bei einer Instanz.
 */
export function nachSicht<T>(
  eintraege: T[],
  istAktiv: (e: T) => boolean,
  sicht: ListenSicht,
): { gefiltert: T[]; zaehler: Record<ListenSicht, number> } {
  const aktiv = eintraege.filter(istAktiv);
  const inaktiv = eintraege.filter((e) => !istAktiv(e));
  return {
    gefiltert: sicht === 'aktiv' ? aktiv : sicht === 'inaktiv' ? inaktiv : eintraege,
    zaehler: { aktiv: aktiv.length, inaktiv: inaktiv.length, alle: eintraege.length },
  };
}
